import { useEffect, useState } from "react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { Plus, KeyRound, UserCog, Timer, Save, FileText, Send, Eye, Coins, Globe, Smartphone, Laptop, Tablet, Monitor, Download, AlertTriangle, Network, Copy, Users as UsersIcon, Heart, Archive, PlayCircle } from "lucide-react";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/shared";
import { ADMIN_ROLES, ROLE_LABELS, STATUT_LABELS } from "@/lib/rbac";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { QRCodeSVG } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

function Users() {
  const { t } = useLang();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", nom: "", role: "technicien" });

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/meta/roles").then((r) => setRoles(r.data)).catch(() => {}); }, []);

  const submit = async () => {
    try { await api.post("/users", form); toast.success(t("ad_user_created")); setOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const resetTotp = async (uid) => {
    try { await api.post(`/users/${uid}/reset-totp`); toast.success(t("ad_totp_reset")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleActif = async (u) => {
    try { await api.patch(`/users/${u.id}`, { actif: !u.actif }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-user-button" style={{ background: "#0F2942" }}><Plus className="h-4 w-4 mr-1" /> {t("ad_new_user")}</Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader><DialogTitle>{t("ad_new_user")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t("ad_full_name")}</Label><Input data-testid="user-nom-input" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} /></div>
              <div><Label>{t("ad_email")}</Label><Input type="email" data-testid="user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>{t("ad_password")}</Label><Input type="password" data-testid="user-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div><Label>{t("ad_role")}</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="role-selector-dropdown"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {roles.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}{r.totp_obligatoire ? " · TOTP" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="user-save-button" style={{ background: "#0284C7" }}>{t("ad_create")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Card className="border-slate-200">
        <Table className="clin-table">
          <TableHeader><TableRow>
            <TableHead>{t("ad_th_nom")}</TableHead><TableHead>{t("ad_email")}</TableHead><TableHead>{t("ad_role")}</TableHead>
            <TableHead>TOTP</TableHead><TableHead>{t("ad_actif")}</TableHead><TableHead>{t("ad_actions")}</TableHead>
          </TableRow></TableHeader>
          <TableBody data-testid="users-table-body">
            {users.map((u) => (
              <TableRow key={u.id} className="h-9">
                <TableCell className="font-medium">{u.nom}</TableCell>
                <TableCell className="font-mono text-xs">{u.email}</TableCell>
                <TableCell><Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200">{ROLE_LABELS[u.role] || u.role}</Badge></TableCell>
                <TableCell>{u.totp_enabled ? <Badge className="bg-emerald-600 text-[10px]">{t("ad_activated")}</Badge> : <span className="text-xs text-slate-400">—</span>}</TableCell>
                <TableCell><button onClick={() => toggleActif(u)} className="text-xs underline text-slate-500">{u.actif ? t("ad_yes") : t("ad_no")}</button></TableCell>
                <TableCell>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => resetTotp(u.id)} data-testid={`reset-totp-${u.email}`}>
                    <KeyRound className="h-3 w-3 mr-1" /> {t("ad_reset_totp")}</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function Audit() {
  const { t } = useLang();
  const [events, setEvents] = useState([]);
  const [pays, setPays] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const load = () => {
    const params = new URLSearchParams({ limit: "200" });
    if (pays) params.set("pays", pays);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    api.get(`/audit?${params.toString()}`).then((r) => setEvents(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const exportAudit = (format) => {
    const params = new URLSearchParams({ format });
    if (pays) params.set("pays", pays);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    downloadFile(`/audit/export?${params.toString()}`, `journal-audit.${format}`)
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  return (
    <Card className="border-slate-200 p-3">
      <div className="flex flex-wrap items-end gap-2 mb-3" data-testid="audit-filters">
        <div className="w-32">
          <Label className="text-xs text-slate-500">{t("ad_cx_country_code")}</Label>
          <Input data-testid="audit-pays" value={pays} onChange={(e) => setPays(e.target.value.toUpperCase())}
                 onKeyDown={(e) => e.key === "Enter" && load()} placeholder={t("ad_cx_country_ph")} maxLength={2} />
        </div>
        <div className="w-36">
          <Label className="text-xs text-slate-500">{t("ad_date_from")}</Label>
          <Input type="date" data-testid="audit-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-10" />
        </div>
        <div className="w-36">
          <Label className="text-xs text-slate-500">{t("ad_date_to")}</Label>
          <Input type="date" data-testid="audit-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-10" />
        </div>
        <Button data-testid="audit-search-btn" onClick={load} variant="secondary" className="h-10">{t("ad_search")}</Button>
        <Button data-testid="audit-export-csv" onClick={() => exportAudit("csv")} className="h-10"><Download className="h-4 w-4 mr-1" />{t("ad_export_csv")}</Button>
        <Button data-testid="audit-export-pdf" onClick={() => exportAudit("pdf")} className="h-10 bg-rose-600 hover:bg-rose-700"><FileText className="h-4 w-4 mr-1" />{t("ad_export_pdf")}</Button>
      </div>
      <Table className="clin-table" data-testid="audit-log-table">
        <TableHeader><TableRow>
          <TableHead>{t("ad_au_ts")}</TableHead><TableHead>{t("ad_au_actor")}</TableHead><TableHead>{t("ad_role")}</TableHead>
          <TableHead>{t("ad_au_action")}</TableHead><TableHead>{t("ad_au_entity")}</TableHead>
          <TableHead>{t("ad_cx_th_city")}</TableHead><TableHead>{t("ad_cx_th_country")}</TableHead><TableHead>IP</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {events.map((e) => (
            <TableRow key={e.id} className="h-9">
              <TableCell className="font-mono text-[11px] whitespace-nowrap">{fmtQc(e.timestamp)}</TableCell>
              <TableCell className="text-xs">{e.actor_email || "—"}</TableCell>
              <TableCell className="text-xs">{e.actor_role || "—"}</TableCell>
              <TableCell><Badge variant="outline" className="text-[10px]">{e.action}</Badge></TableCell>
              <TableCell className="text-xs">{e.entity || "—"}</TableCell>
              <TableCell className="text-xs">{e.city || "—"}</TableCell>
              <TableCell className="text-xs whitespace-nowrap">{ccFlag(e.country_code)} {e.country || "—"}</TableCell>
              <TableCell className="font-mono text-[11px]">{e.ip || "—"}</TableCell>
            </TableRow>
          ))}
          {events.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">{t("ad_au_empty")}</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Card>
  );
}

function Objectifs() {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const load = () => api.get("/phase-objectifs").then((r) => setRows(r.data.phases)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setHours = (statut, val) => {
    const sec = Math.max(0, Math.round(parseFloat(val || 0) * 3600));
    setRows((rs) => rs.map((r) => (r.statut === statut ? { ...r, cible_sec: sec } : r)));
  };
  const setSeuil = (statut, val) => {
    const pct = Math.min(100, Math.max(1, Math.round(parseFloat(val || 0))));
    setRows((rs) => rs.map((r) => (r.statut === statut ? { ...r, seuil_pct: pct } : r)));
  };
  const save = async () => {
    const objectifs = {}; const seuils = {};
    rows.forEach((r) => { objectifs[r.statut] = r.cible_sec; seuils[r.statut] = r.seuil_pct; });
    try { await api.put("/phase-objectifs", { objectifs, seuils }); toast.success(t("ad_obj_saved")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card className="border-slate-200 p-5" data-testid="objectifs-card">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-600 max-w-2xl">
          {t("ad_obj_desc1")}<span className="text-red-600 font-semibold">{t("ad_obj_red")}</span>{t("ad_obj_desc2")}
        </p>
        <Button onClick={save} data-testid="objectifs-save-button" style={{ background: "#0284C7" }}>
          <Save className="h-4 w-4 mr-1" /> {t("ad_save")}
        </Button>
      </div>
      <Table className="clin-table">
        <TableHeader><TableRow>
          <TableHead>{t("ad_obj_phase")}</TableHead><TableHead className="w-44">{t("ad_obj_cible")}</TableHead><TableHead className="w-52">{t("ad_obj_seuil")}</TableHead>
        </TableRow></TableHeader>
        <TableBody data-testid="objectifs-table-body">
          {rows.map((r) => (
            <TableRow key={r.statut} className="h-10">
              <TableCell className="font-medium text-slate-800">{r.label}</TableCell>
              <TableCell>
                <Input type="number" min="0" step="0.5"
                       data-testid={`objectif-input-${r.statut}`}
                       value={(r.cible_sec / 3600).toString()}
                       onChange={(e) => setHours(r.statut, e.target.value)}
                       className="max-w-[120px]" />
              </TableCell>
              <TableCell>
                <Input type="number" min="1" max="100" step="1"
                       data-testid={`seuil-input-${r.statut}`}
                       value={(r.seuil_pct ?? 80).toString()}
                       onChange={(e) => setSeuil(r.statut, e.target.value)}
                       className="max-w-[110px]" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function Rapport() {
  const { t } = useLang();
  const [dest, setDest] = useState("");
  const [defaut, setDefaut] = useState([]);
  const [reports, setReports] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [month, setMonth] = useState("");
  const [tendance, setTendance] = useState([]);
  const [phaseSel, setPhaseSel] = useState("");
  const [recap, setRecap] = useState({ jour: 0, heure: 7, enabled: true });

  const loadDest = () => api.get("/rapport-destinataires").then((r) => { setDest((r.data.emails || []).join("\n")); setDefaut(r.data.defaut || []); }).catch(() => {});
  const loadReports = () => api.get("/rapports-mensuels").then((r) => setReports(r.data)).catch(() => {});
  const loadTendance = (statut) => api.get("/stats/tendance", { params: { mois: 6, ...(statut ? { statut } : {}) } })
    .then((r) => setTendance((r.data || []).map((d) => ({ ...d, moy_j: +(d.moy_sec / 86400).toFixed(1) })))).catch(() => {});
  const loadRecap = () => api.get("/recap-config").then((r) => setRecap(r.data)).catch(() => {});
  useEffect(() => { loadDest(); loadReports(); loadTendance(""); loadRecap(); }, []);

  const onPhase = (v) => { const s = v === "__all__" ? "" : v; setPhaseSel(s); loadTendance(s); };
  const saveRecap = async () => {
    try { const r = await api.put("/recap-config", recap); setRecap(r.data); toast.success(t("ad_rp_recap_saved")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const JOURS = [t("ad_day_lun"), t("ad_day_mar"), t("ad_day_mer"), t("ad_day_jeu"), t("ad_day_ven"), t("ad_day_sam"), t("ad_day_dim")];
  const barKey = phaseSel ? "count" : "depassements";
  const barName = phaseSel ? t("ad_rp_volume") : t("ad_rp_depass");

  const saveDest = async () => {
    const emails = dest.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
    try { const r = await api.put("/rapport-destinataires", { emails }); setDest((r.data.emails || []).join("\n")); toast.success(t("ad_rp_dest_saved")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const generer = async () => {
    try { await api.post("/rapports-mensuels/generer", month ? { month } : {}); toast.success(t("ad_rp_generated")); loadReports(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const view = async (m) => {
    try { const r = await api.get(`/rapports-mensuels/${m}`); setViewing(r.data); }
    catch (e) { toast.error(t("ad_rp_notfound")); }
  };
  const fmtH = (sec) => sec == null ? "—" : (sec / 3600 >= 24 ? (sec / 3600 / 24).toFixed(1) + " " + t("ad_unit_j") : (sec / 3600).toFixed(1) + " " + t("ad_unit_h"));
  const exportR = (m, fmt) => downloadFile(`/stats/performance/export?format=${fmt}&month=${m}`, `performance-${m}.${fmt}`).catch(() => toast.error(t("db_export_err")));

  return (
    <div className="space-y-6" data-testid="rapport-card">
      <Card className="border-slate-200 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <h3 className="font-semibold text-slate-800">{t("ad_rp_trend_title")}</h3>
          <Select value={phaseSel || "__all__"} onValueChange={onPhase}>
            <SelectTrigger className="w-56" data-testid="tendance-phase-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="__all__">{t("ad_rp_global")}</SelectItem>
              {Object.entries(STATUT_LABELS).map(([code, lbl]) => <SelectItem key={code} value={code}>{lbl}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <p className="text-sm text-slate-500 mb-3">{phaseSel ? `${t("ad_rp_phase_desc_a")}${STATUT_LABELS[phaseSel] || phaseSel}${t("ad_rp_phase_desc_b")}` : t("ad_rp_global_desc")}</p>
        <div style={{ width: "100%", height: 280 }} data-testid="tendance-chart">
          <ResponsiveContainer>
            <ComposedChart data={tendance} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="l" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="r" orientation="right" allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar yAxisId="r" dataKey={barKey} name={barName} fill="#fca5a5" radius={[4, 4, 0, 0]} />
              <Line yAxisId="l" type="monotone" dataKey="moy_j" name={t("ad_rp_delai_moy")} stroke="#0284C7" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="border-slate-200 p-5" data-testid="recap-config-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold text-slate-800">{t("ad_rp_recap_title")}</h3>
            <p className="text-sm text-slate-500">{t("ad_rp_recap_desc")}</p>
          </div>
          <Button onClick={saveRecap} data-testid="recap-save-button" style={{ background: "#0284C7" }}><Save className="h-4 w-4 mr-1" />{t("ad_save")}</Button>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={!!recap.enabled} onChange={(e) => setRecap({ ...recap, enabled: e.target.checked })} data-testid="recap-enabled-checkbox" />
            {t("ad_activated")}
          </label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">{t("ad_rp_jour")}</span>
            <Select value={String(recap.jour)} onValueChange={(v) => setRecap({ ...recap, jour: parseInt(v, 10) })}>
              <SelectTrigger className="w-36" data-testid="recap-jour-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {JOURS.map((j, i) => <SelectItem key={j} value={String(i)}>{j}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">{t("ad_rp_heure")}</span>
            <Select value={String(recap.heure)} onValueChange={(v) => setRecap({ ...recap, heure: parseInt(v, 10) })}>
              <SelectTrigger className="w-28" data-testid="recap-heure-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white max-h-64">
                {Array.from({ length: 24 }, (_, h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold text-slate-800">{t("ad_rp_dest_title")}</h3>
            <p className="text-sm text-slate-500">{t("ad_rp_dest_desc")}</p>
          </div>
          <Button onClick={saveDest} data-testid="dest-save-button" style={{ background: "#0284C7" }}><Save className="h-4 w-4 mr-1" />{t("ad_save")}</Button>
        </div>
        <textarea data-testid="dest-textarea" value={dest} onChange={(e) => setDest(e.target.value)} rows={4}
                  placeholder="comite.qualite@laboratoire.ca" className="w-full border border-slate-200 rounded-md px-3 py-2 font-mono text-sm" />
        {defaut.length > 0 && <p className="text-xs text-slate-400 mt-2">{t("ad_rp_dest_default")}{defaut.join(", ")}</p>}
      </Card>

      <Card className="border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-800">{t("ad_rp_hist_title")}</h3>
          <div className="flex items-center gap-2">
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="rapport-month-input"
                   className="border border-slate-200 rounded-md px-2 py-1 text-sm text-slate-700" />
            <Button variant="outline" size="sm" onClick={generer} data-testid="rapport-generer-button"><Send className="h-4 w-4 mr-1" />{t("ad_rp_gen_month")}</Button>
          </div>
        </div>
        <Table className="clin-table">
          <TableHeader><TableRow>
            <TableHead>{t("ad_rp_th_mois")}</TableHead><TableHead>{t("ad_rp_th_gen")}</TableHead><TableHead>{t("ad_rp_depass")}</TableHead><TableHead>{t("ad_rp_th_envois")}</TableHead><TableHead>{t("ad_actions")}</TableHead>
          </TableRow></TableHeader>
          <TableBody data-testid="rapports-table-body">
            {reports.map((r) => (
              <TableRow key={r.month} className="h-10">
                <TableCell className="font-medium">{r.month}</TableCell>
                <TableCell className="text-xs">{r.generated_at?.slice(0, 16).replace("T", " ")}</TableCell>
                <TableCell>{r.depassements > 0 ? <Badge className="bg-red-600 text-[10px]">{r.depassements}</Badge> : <span className="text-slate-400 text-xs">0</span>}</TableCell>
                <TableCell className="text-xs">{r.envoyes ?? "—"}</TableCell>
                <TableCell className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => view(r.month)} data-testid={`rapport-view-${r.month}`}><Eye className="h-3 w-3 mr-1" />{t("ad_view")}</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => exportR(r.month, "pdf")} data-testid={`rapport-pdf-${r.month}`}>PDF</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => exportR(r.month, "csv")} data-testid={`rapport-csv-${r.month}`}>CSV</Button>
                </TableCell>
              </TableRow>
            ))}
            {reports.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-6">{t("ad_rp_empty")}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      {viewing && (
        <Card className="border-slate-200 p-5" data-testid="rapport-detail">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800">{t("ad_rp_detail")}{viewing.month}</h3>
            <Button size="sm" variant="ghost" onClick={() => setViewing(null)} data-testid="rapport-detail-close">{t("ad_close")}</Button>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-2">{t("ad_rp_delais_phase")}</div>
              <ul className="text-sm space-y-1">
                {(viewing.data?.phases || []).map((p) => (
                  <li key={p.statut} className="flex justify-between border-b border-slate-100 py-1">
                    <span>{p.label}</span>
                    <span className={p.depasse ? "font-mono text-red-600 font-semibold" : "font-mono text-slate-600"}>{fmtH(p.moy_sec)}{p.cible_sec != null && <span className="text-slate-400"> / {fmtH(p.cible_sec)}</span>}</span>
                  </li>
                ))}
                {(viewing.data?.phases || []).length === 0 && <li className="text-slate-400">{t("ad_rp_no_data")}</li>}
              </ul>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-2">{t("ad_rp_patho")}</div>
              <ul className="text-sm space-y-1">
                {(viewing.data?.pathologistes || []).map((p) => (
                  <li key={p.par} className="flex justify-between border-b border-slate-100 py-1"><span className="truncate max-w-[55%]">{p.par}</span><span className="text-slate-600">{p.valide} · {fmtH(p.moy_sec)}</span></li>
                ))}
                {(viewing.data?.pathologistes || []).length === 0 && <li className="text-slate-400">{t("ad_rp_no_cr")}</li>}
              </ul>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

function Couts() {
  const { t } = useLang();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/couts").then((r) => setD(r.data)).catch((e) => setErr(formatApiError(e.response?.data?.detail)));
  }, []);
  if (err) return <div className="text-sm text-red-600" data-testid="couts-error">{err}</div>;
  if (!d) return <div className="text-sm text-slate-400" data-testid="couts-loading">{t("dd_loading")}</div>;
  const u = d.usage, e = d.estimation;
  const Stat = ({ label, value, sub, testid }) => (
    <Card className="p-4" data-testid={testid}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </Card>
  );
  const Row = ({ label, val, bold }) => (
    <div className={`flex justify-between py-1.5 border-b border-slate-100 ${bold ? "font-bold text-slate-900" : "text-slate-600"}`}>
      <span>{label}</span><span>{typeof val === "number" ? `${val.toFixed(2)} $` : val}</span>
    </div>
  );
  return (
    <div className="space-y-5" data-testid="couts-panel">
      <div className="text-sm text-slate-500">
        {t("ad_ct_conso1")}<span className="font-semibold text-slate-700">{d.mois}</span>{t("ad_ct_conso2")}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat testid="couts-courriels" label={t("ad_ct_emails_sent")} value={u.courriels}
              sub={d.suivi_courriels_actif ? t("ad_ct_since_track") : t("ad_ct_track_on")} />
        <Stat testid="couts-analyses" label={t("ad_ct_ia_launched")} value={u.analyses_ia} sub={t("ad_ct_ia_sim")} />
        <Stat testid="couts-lames" label={t("ad_ct_lames")} value={u.lames} />
        <Stat testid="couts-demandes" label={t("ad_ct_demandes")} value={u.demandes} />
      </div>
      <Card className="p-4 max-w-md" data-testid="couts-estimation">
        <div className="text-sm font-semibold text-slate-700 mb-2">{t("ad_ct_est_month")}</div>
        <Row label={t("ad_ct_hosting")} val={e.hebergement_usd} />
        <Row label={t("ad_ct_emails")} val={e.courriels_usd} />
        <Row label={t("ad_ct_ia_sim2")} val={e.ia_usd} />
        <Row label={t("ad_ct_total")} val={e.total_mensuel_usd} bold />
        <div className="text-xs text-slate-400 mt-2">
          {t("ad_ct_domaine_a")}{e.domaine_annuel_usd_min}-{e.domaine_annuel_usd_max}{t("ad_ct_domaine_b")}{d.tarifs.resend_gratuit_par_mois.toLocaleString()}{t("ad_ct_domaine_c")}
        </div>
      </Card>
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 max-w-2xl" data-testid="couts-note-ia">
        ⚠️ {t("ad_ct_note_ia_a")}<b>{t("ad_ct_note_ia_sim")}</b>{t("ad_ct_note_ia_b")}<b>COUTS.md</b>{t("ad_ct_note_ia_c")}
      </div>
    </div>
  );
}

function ccFlag(cc) {
  if (!cc || cc.length !== 2) return "";
  try { return String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0))); }
  catch { return ""; }
}
const DEVICE_ICON = { iPhone: Smartphone, iPad: Tablet, "Android (téléphone)": Smartphone, "Android (tablette)": Tablet, Mobile: Smartphone, Chromebook: Laptop };
function deviceIcon(dev) {
  if (DEVICE_ICON[dev]) return DEVICE_ICON[dev];
  if (dev && dev.startsWith("Ordinateur")) return Laptop;
  return Monitor;
}

const GEO_URL = "/world-110m.json";

// Formate un horodatage ISO (UTC) à l'heure du Québec (Heure de l'Est, DST automatique)
function fmtQc(iso) {
  if (!iso) return "—";
  let s = String(iso);
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s += "Z";
  const d = new Date(s);
  if (isNaN(d.getTime())) return String(iso).slice(0, 19).replace("T", " ");
  try {
    const f = new Intl.DateTimeFormat("sv-SE", {
      timeZone: "America/Toronto", year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    }).format(d);
    return `${f} HE`;
  } catch {
    return String(iso).slice(0, 19).replace("T", " ");
  }
}

function WorldMap() {
  const { t } = useLang();
  const [pts, setPts] = useState([]);
  useEffect(() => { api.get("/connexions/geo").then((r) => setPts(r.data.points || [])).catch(() => {}); }, []);
  const max = Math.max(1, ...pts.map((p) => p.count));
  const totalSuspect = pts.reduce((a, p) => a + (p.suspect || 0), 0);
  return (
    <Card className="p-3" data-testid="connexions-map">
      <div className="text-xs font-semibold text-slate-500 mb-1">{t("ad_cx_geo_title")}</div>
      <div className="bg-slate-50 rounded-lg overflow-hidden">
        <ComposableMap projectionConfig={{ scale: 145 }} height={360} style={{ width: "100%", height: "auto" }} data-testid="world-map-svg">
          <Geographies geography={GEO_URL}>
            {({ geographies }) => geographies.map((geo) => (
              <Geography key={geo.rsmKey} geography={geo} fill="#E2E8F0" stroke="#CBD5E1" strokeWidth={0.4}
                style={{ default: { outline: "none" }, hover: { fill: "#CBD5E1", outline: "none" }, pressed: { outline: "none" } }} />
            ))}
          </Geographies>
          {pts.map((p) => {
            const r = 4 + 12 * Math.sqrt(p.count / max);
            const color = p.suspect > 0 ? "#D97706" : "#0284C7";
            return (
              <Marker key={p.country_code} coordinates={[p.lon, p.lat]} data-testid="map-marker">
                <circle r={r} fill={color} fillOpacity={0.45} stroke={color} strokeWidth={1.2} />
                <title>{p.country} : {p.count}{t("ad_cx_conns")}{p.suspect > 0 ? ` · ${p.suspect}${t("ad_cx_unusual")}` : ""}</title>
              </Marker>
            );
          })}
        </ComposableMap>
      </div>
      <div className="flex flex-wrap gap-4 text-[11px] text-slate-500 mt-1.5">
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "#0284C7" }} />{t("ad_cx_normal")}</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "#D97706" }} />{t("ad_cx_unusual_countries")}{totalSuspect > 0 ? ` (${totalSuspect})` : ""}</span>
        <span className="text-slate-400">{t("ad_cx_dot_size")}</span>
      </div>
    </Card>
  );
}

function Connexions() {
  const { t } = useLang();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [pays, setPays] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const load = () => {
    const params = new URLSearchParams({ limit: "300" });
    if (q) params.set("q", q);
    if (pays) params.set("pays", pays);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    api.get(`/connexions?${params.toString()}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const exportCsv = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (pays) params.set("pays", pays);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    downloadFile(`/connexions/export?${params.toString()}`, "connexions.csv")
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  if (!rows) return <div className="text-sm text-slate-400" data-testid="connexions-loading">{t("dd_loading")}</div>;
  const suspects = rows.filter((r) => r.suspect).length;
  return (
    <div className="space-y-3" data-testid="connexions-panel">
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-xs text-slate-500">{t("ad_cx_search_label")}</Label>
          <Input data-testid="connexions-search" value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && load()} placeholder={t("ad_cx_search_ph")} />
        </div>
        <div className="w-32">
          <Label className="text-xs text-slate-500">{t("ad_cx_country_code")}</Label>
          <Input data-testid="connexions-pays" value={pays} onChange={(e) => setPays(e.target.value.toUpperCase())}
                 onKeyDown={(e) => e.key === "Enter" && load()} placeholder={t("ad_cx_country_ph")} maxLength={2} />
        </div>
        <div className="w-36">
          <Label className="text-xs text-slate-500">{t("ad_date_from")}</Label>
          <Input type="date" data-testid="connexions-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-10" />
        </div>
        <div className="w-36">
          <Label className="text-xs text-slate-500">{t("ad_date_to")}</Label>
          <Input type="date" data-testid="connexions-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-10" />
        </div>
        <Button data-testid="connexions-search-btn" onClick={load} variant="secondary" className="h-10">{t("ad_search")}</Button>
        <Button data-testid="connexions-export-btn" onClick={exportCsv} className="h-10">
          <Download className="h-4 w-4 mr-1" />{t("ad_export_csv")}
        </Button>
      </div>
      <div className="text-sm text-slate-500">
        {rows.length}{t("ad_cx_conns")}{suspects > 0 && <span className="text-amber-700 font-medium"> · {suspects}{t("ad_cx_unusual")}</span>}{t("ad_cx_desc_suffix")}
      </div>
      <WorldMap />
      <Table className="clin-table" data-testid="connexions-table">
        <TableHeader><TableRow>
          <TableHead>{t("ad_cx_th_date")}</TableHead><TableHead>{t("ad_cx_th_user")}</TableHead><TableHead>{t("ad_role")}</TableHead>
          <TableHead>{t("ad_cx_th_device")}</TableHead><TableHead>{t("ad_cx_th_ip")}</TableHead>
          <TableHead>{t("ad_cx_th_city")}</TableHead><TableHead>{t("ad_cx_th_country")}</TableHead><TableHead>{t("ad_cx_th_coord")}</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((r) => {
            const Ic = deviceIcon(r.device);
            const hasCoord = r.lat != null && r.lon != null;
            return (
              <TableRow key={r.id} className={`h-9 ${r.suspect ? "bg-amber-50" : ""}`} data-testid="connexion-row">
                <TableCell className="font-mono text-[11px] whitespace-nowrap">
                  {r.suspect && <AlertTriangle className="inline h-3.5 w-3.5 text-amber-600 mr-1" title={(r.suspect_raisons || []).join(" · ")} />}
                  {fmtQc(r.created_at)}
                </TableCell>
                <TableCell className="text-xs">
                  {r.nom || r.email}
                  {r.is_test && <Badge className="ml-1 bg-slate-400 text-[9px] px-1 py-0">TEST</Badge>}
                </TableCell>
                <TableCell><Badge variant="outline" className="text-[10px] bg-sky-50 text-sky-700 border-sky-200">{ROLE_LABELS[r.role] || r.role}</Badge></TableCell>
                <TableCell className="text-xs">
                  <span className="inline-flex items-center gap-1"><Ic className="h-3.5 w-3.5 text-sky-600" />{r.device || "—"}</span>
                  <div className="text-[10px] text-slate-400">{[r.os, r.browser].filter(Boolean).join(" · ") || "—"}</div>
                </TableCell>
                <TableCell className="font-mono text-[11px]">{r.ip || "—"}</TableCell>
                <TableCell className="text-xs">{r.city || "—"}</TableCell>
                <TableCell className="text-xs whitespace-nowrap">{ccFlag(r.country_code)} {r.country || "—"}</TableCell>
                <TableCell className="font-mono text-[10px] text-slate-500 whitespace-nowrap">{hasCoord ? `${r.lat.toFixed(3)}, ${r.lon.toFixed(3)}` : "—"}</TableCell>
              </TableRow>
            );
          })}
          {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">{t("ad_cx_empty")}</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}

function VisitesFondationMap() {
  const { t } = useLang();
  const [pts, setPts] = useState([]);
  useEffect(() => { api.get("/fondation/visites/geo").then((r) => setPts(r.data.points || [])).catch(() => {}); }, []);
  const max = Math.max(1, ...pts.map((p) => p.count));
  return (
    <Card className="p-3" data-testid="visites-map">
      <div className="text-xs font-semibold text-slate-500 mb-1">{t("vf_geo_title")}</div>
      <div className="bg-slate-50 rounded-lg overflow-hidden">
        <ComposableMap projectionConfig={{ scale: 145 }} height={360} style={{ width: "100%", height: "auto" }}>
          <Geographies geography={GEO_URL}>
            {({ geographies }) => geographies.map((geo) => (
              <Geography key={geo.rsmKey} geography={geo} fill="#E2E8F0" stroke="#CBD5E1" strokeWidth={0.4}
                style={{ default: { outline: "none" }, hover: { fill: "#CBD5E1", outline: "none" }, pressed: { outline: "none" } }} />
            ))}
          </Geographies>
          {pts.map((p) => {
            const r = 4 + 12 * Math.sqrt(p.count / max);
            return (
              <Marker key={p.country_code} coordinates={[p.lon, p.lat]} data-testid="visite-marker">
                <circle r={r} fill="#DB2777" fillOpacity={0.4} stroke="#DB2777" strokeWidth={1.2} />
                <title>{p.country} : {p.count}</title>
              </Marker>
            );
          })}
        </ComposableMap>
      </div>
      <div className="text-[11px] text-slate-400 mt-1.5">{t("vf_dot_size")}</div>
    </Card>
  );
}

const DEMO_ST = { nouvelle: "Nouvelle", confirmee: "Confirmée", terminee: "Terminée", annulee: "Annulée" };

function DemandesDemo() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/video/demos").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setSt = (id, statut) => api.patch(`/video/demos/${id}`, { statut }).then(load).catch(() => toast.error("Erreur"));
  return (
    <Card className="p-4" data-testid="demo-requests-card">
      <div className="text-sm font-semibold mb-2">Demandes de démonstration ({rows.length})</div>
      <Table className="clin-table" data-testid="demo-requests-table">
        <TableHeader><TableRow>
          <TableHead>Reçue</TableHead><TableHead>Nom / poste</TableHead><TableHead>Établissement</TableHead><TableHead>Contact</TableHead>
          <TableHead>1er / 2e choix (HE)</TableHead><TableHead>Message</TableHead><TableHead>Statut</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((d) => (
            <TableRow key={d.id} data-testid="demo-request-row">
              <TableCell className="font-mono text-[11px] whitespace-nowrap">{fmtQc(d.created_at)}</TableCell>
              <TableCell className="text-xs"><div className="font-semibold">{d.nom}</div><div className="text-slate-500">{d.poste || "—"}</div></TableCell>
              <TableCell className="text-xs">{d.etablissement}<div className="text-slate-500">{ccFlag(d.country_code)} {[d.city, d.country].filter(Boolean).join(", ")}</div></TableCell>
              <TableCell className="text-xs"><a className="text-sky-700 underline" href={`mailto:${d.courriel}`}>{d.courriel}</a><div>{d.telephone || ""}</div></TableCell>
              <TableCell className="text-xs font-mono whitespace-nowrap">{d.creneau1?.replace("T", " ")}<br />{d.creneau2?.replace("T", " ")}</TableCell>
              <TableCell className="text-xs max-w-[220px] whitespace-pre-wrap">{d.message || "—"}</TableCell>
              <TableCell>
                <Select value={d.statut} onValueChange={(v) => setSt(d.id, v)}>
                  <SelectTrigger className="h-8 w-32 text-xs" data-testid={`demo-status-${d.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(DEMO_ST).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-6">Aucune demande pour le moment.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Card>
  );
}

function VideoVues() {
  const { t } = useLang();
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);
  const [pays, setPays] = useState("");
  const [lang, setLang] = useState("");
  const load = () => {
    const p = new URLSearchParams({ limit: "400" });
    if (pays) p.set("pays", pays);
    if (lang) p.set("lang", lang);
    api.get(`/video/vues?${p.toString()}`).then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get(`/video/vues/stats?${p.toString()}`).then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    /* eslint-disable-next-line */
  }, []);
  if (!rows) return <div className="text-sm text-slate-400">{t("dd_loading")}</div>;
  const LN = { fr: "Français", en: "English", de: "Deutsch", ar: "العربية" };
  return (
    <div className="space-y-3" data-testid="video-vues-panel">
      <DemandesDemo />
      {stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {[[t("vv_clics"), stats.clics, "vv-stat-clics"], [t("vv_total"), stats.total, "vv-stat-total"], [t("vf_uniques"), stats.uniques, "vv-stat-uniques"], [t("vv_avg"), `${stats.avg_pct}%`, "vv-stat-avg"], [t("vv_complete"), stats.complete, "vv-stat-complete"]].map(([lab, val, id]) => (
              <Card key={id} className="p-4" data-testid={id}>
                <div className="text-xs text-slate-500">{lab}</div>
                <div className="text-2xl font-bold text-slate-900">{val ?? 0}</div>
                {id === "vv-stat-clics" && <div className="text-[11px] text-slate-400" data-testid="vv-stat-clics-uniques">{stats.clics_uniques ?? 0} {t("vf_uniques").toLowerCase()}</div>}
              </Card>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            <Card className="p-4" data-testid="vv-by-lang">
              <div className="text-sm font-semibold mb-2">{t("vv_by_lang")}</div>
              {Object.entries(stats.langs).map(([k, n]) => (
                <div key={k} className="flex items-center gap-2 text-xs py-1">
                  <span className="w-20" style={{ letterSpacing: "normal", fontFamily: k === "ar" ? "'Noto Sans Arabic',Tahoma,sans-serif" : undefined }}>{LN[k]}</span>
                  <div className="flex-1 h-2 rounded bg-slate-100 overflow-hidden"><div className="h-full bg-sky-600" style={{ width: `${stats.total ? (n / stats.total) * 100 : 0}%` }} /></div>
                  <span className="w-8 text-right font-mono">{n}</span>
                </div>
              ))}
            </Card>
            <Card className="p-4" data-testid="vv-by-country">
              <div className="text-sm font-semibold mb-2">{t("vv_by_country")}</div>
              {stats.top_pays.length === 0 && <div className="text-xs text-slate-400">—</div>}
              {stats.top_pays.map((p) => (
                <div key={p.country_code} className="flex justify-between text-xs py-1"><span>{ccFlag(p.country_code)} {p.country || p.country_code}</span><span className="font-mono">{p.count}</span></div>
              ))}
            </Card>
          </div>
        </>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <div className="w-32">
          <Label className="text-xs text-slate-500">{t("ad_cx_country_code")}</Label>
          <Input data-testid="vv-pays" value={pays} onChange={(e) => setPays(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && load()} placeholder={t("ad_cx_country_ph")} maxLength={2} />
        </div>
        <div className="w-40">
          <Label className="text-xs text-slate-500">{t("vf_th_lang")}</Label>
          <Select value={lang || "all"} onValueChange={(v) => setLang(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="vv-lang" className="h-10"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">—</SelectItem>{Object.entries(LN).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button data-testid="vv-search-btn" onClick={load} variant="secondary" className="h-10">{t("ad_search")}</Button>
      </div>
      <Table className="clin-table" data-testid="vv-table">
        <TableHeader><TableRow>
          <TableHead>{t("ad_cx_th_date")}</TableHead><TableHead>{t("vf_th_lang")}</TableHead><TableHead>{t("vv_watched")}</TableHead>
          <TableHead>{t("vv_source")}</TableHead><TableHead>{t("ad_cx_th_device")}</TableHead><TableHead>{t("ad_cx_th_city")}</TableHead><TableHead>{t("ad_cx_th_country")}</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} className="h-9" data-testid="vv-row">
              <TableCell className="font-mono text-[11px] whitespace-nowrap">{fmtQc(r.created_at)}</TableCell>
              <TableCell className="text-xs" style={{ letterSpacing: "normal", fontFamily: r.lang === "ar" ? "'Noto Sans Arabic',Tahoma,sans-serif" : undefined }}>{LN[r.lang] || "—"}</TableCell>
              <TableCell className="text-xs font-mono">{r.pct ?? 0}%</TableCell>
              <TableCell className="text-xs">{r.source || "—"}</TableCell>
              <TableCell className="text-xs">{[r.device, r.browser].filter(Boolean).join(" · ") || "—"}</TableCell>
              <TableCell className="text-xs">{r.city || "—"}</TableCell>
              <TableCell className="text-xs whitespace-nowrap">{ccFlag(r.country_code)} {r.country || "—"}</TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">{t("vv_empty")}</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}

function VisitesFondation() {
  const { t } = useLang();
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [pays, setPays] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const load = () => {
    const params = new URLSearchParams({ limit: "400" });
    if (q) params.set("q", q);
    if (pays) params.set("pays", pays);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    api.get(`/fondation/visites?${params.toString()}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  };
  useEffect(() => {
    load();
    api.get("/fondation/visites/stats").then((r) => setStats(r.data)).catch(() => {});
    /* eslint-disable-next-line */
  }, []);
  const exportCsv = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (pays) params.set("pays", pays);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    downloadFile(`/fondation/visites/export?${params.toString()}`, "visiteurs-fondation.csv")
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  if (!rows) return <div className="text-sm text-slate-400" data-testid="visites-loading">{t("dd_loading")}</div>;
  return (
    <div className="space-y-3" data-testid="visites-fondation-panel">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {[[t("vf_total"), stats.total], [t("vf_uniques"), stats.uniques], [t("vf_last7"), stats.last7], [t("vf_mobiles"), stats.mobiles], [t("vf_pays"), stats.pays_count]].map(([lab, val]) => (
            <Card key={lab} className="p-4">
              <div className="text-xs text-slate-500">{lab}</div>
              <div className="text-2xl font-bold text-slate-900">{val ?? 0}</div>
            </Card>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-xs text-slate-500">{t("vf_search_label")}</Label>
          <Input data-testid="visites-search" value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && load()} placeholder={t("vf_search_ph")} />
        </div>
        <div className="w-32">
          <Label className="text-xs text-slate-500">{t("ad_cx_country_code")}</Label>
          <Input data-testid="visites-pays" value={pays} onChange={(e) => setPays(e.target.value.toUpperCase())}
                 onKeyDown={(e) => e.key === "Enter" && load()} placeholder={t("ad_cx_country_ph")} maxLength={2} />
        </div>
        <div className="w-36">
          <Label className="text-xs text-slate-500">{t("ad_date_from")}</Label>
          <Input type="date" data-testid="visites-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-10" />
        </div>
        <div className="w-36">
          <Label className="text-xs text-slate-500">{t("ad_date_to")}</Label>
          <Input type="date" data-testid="visites-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-10" />
        </div>
        <Button data-testid="visites-search-btn" onClick={load} variant="secondary" className="h-10">{t("ad_search")}</Button>
        <Button data-testid="visites-export-btn" onClick={exportCsv} className="h-10">
          <Download className="h-4 w-4 mr-1" />{t("ad_export_csv")}
        </Button>
      </div>
      <div className="text-sm text-slate-500">{rows.length}{t("vf_count_suffix")}</div>
      <VisitesFondationMap />
      <Table className="clin-table" data-testid="visites-table">
        <TableHeader><TableRow>
          <TableHead>{t("ad_cx_th_date")}</TableHead><TableHead>{t("ad_cx_th_device")}</TableHead>
          <TableHead>{t("vf_th_lang")}</TableHead><TableHead>{t("ad_cx_th_ip")}</TableHead>
          <TableHead>{t("ad_cx_th_city")}</TableHead><TableHead>{t("ad_cx_th_country")}</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((r) => {
            const Ic = deviceIcon(r.device);
            return (
              <TableRow key={r.id} className="h-9" data-testid="visite-row">
                <TableCell className="font-mono text-[11px] whitespace-nowrap">{fmtQc(r.created_at)}</TableCell>
                <TableCell className="text-xs">
                  <span className="inline-flex items-center gap-1"><Ic className="h-3.5 w-3.5 text-pink-600" />{r.device || "—"}</span>
                  <div className="text-[10px] text-slate-400">{[r.os, r.browser].filter(Boolean).join(" · ") || "—"}</div>
                </TableCell>
                <TableCell className="text-xs uppercase">{r.lang || "—"}</TableCell>
                <TableCell className="font-mono text-[11px]">{r.ip || "—"}</TableCell>
                <TableCell className="text-xs">{r.city || "—"}</TableCell>
                <TableCell className="text-xs whitespace-nowrap">{ccFlag(r.country_code)} {r.country || "—"}</TableCell>
              </TableRow>
            );
          })}
          {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">{t("vf_empty")}</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}

function Recommandations() {
  const { t } = useLang();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/recommandations").then((r) => setD(r.data)).catch(() => setD({ forest: [], stats: {}, top: [] }));
  }, []);
  const myUrl = `${window.location.origin}/?ref=${user?.id || ""}`;
  const copyMy = async () => {
    try { await navigator.clipboard.writeText(myUrl); toast.success(t("ad_rc_link_copied")); }
    catch { toast.error(t("ad_rc_copy_err")); }
  };
  if (!d) return <div className="text-sm text-slate-400" data-testid="reco-loading">{t("dd_loading")}</div>;
  const s = d.stats || {};
  const Stat = ({ label, value, sub }) => (
    <Card className="p-4"><div className="text-xs text-slate-500">{label}</div><div className="text-2xl font-bold text-slate-900">{value}</div>{sub && <div className="text-[11px] text-slate-400">{sub}</div>}</Card>
  );
  return (
    <div className="space-y-5" data-testid="recommandations-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label={t("ad_rc_clics")} value={s.total_clics ?? 0} sub={`${s.total_clics_uniques ?? 0}${t("ad_rc_unique_visitors_a")}`} />
        <Stat label={t("ad_rc_parrains")} value={s.nb_parrains ?? 0} />
        <Stat label={t("ad_rc_filleuls")} value={s.nb_filleuls ?? 0} />
        <Stat label={t("ad_rc_depth")} value={s.profondeur_max ?? 0} />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="p-4 md:col-span-2" data-testid="reco-tree">
          <div className="text-sm font-semibold text-slate-700 mb-2">{t("ad_rc_tree")}</div>
          {(() => {
            const flat = [];
            const walk = (nodes, depth) => (nodes || []).forEach((n) => { flat.push({ ...n, depth }); walk(n.children, depth + 1); });
            walk(d.forest, 0);
            return flat.length
              ? flat.map((n) => (
                <div key={n.id} className="flex items-center gap-2 py-1" style={{ paddingLeft: n.depth * 18 }} data-testid="ref-node">
                  {n.depth > 0 && <span className="text-slate-300">└</span>}
                  <span className="text-sm font-medium text-slate-800">{n.nom}</span>
                  <Badge variant="outline" className="text-[10px] bg-sky-50 text-sky-700 border-sky-200">{ROLE_LABELS[n.role] || n.role}</Badge>
                  <span className="text-[11px] text-slate-500">· {n.clics}{t("ad_rc_clic_s")}{n.clics_uniques ? ` (${n.clics_uniques}${t("ad_rc_uniq")})` : ""}</span>
                  {n.filleuls > 0 && <span className="text-[11px] text-teal-700">· {n.filleuls}{t("ad_rc_filleul_s")}, {n.subtree}{t("ad_rc_total")}</span>}
                </div>
              ))
              : <div className="text-sm text-slate-400">{t("ad_rc_no_reco")}</div>;
          })()}
        </Card>
        <Card className="p-4 flex flex-col items-center text-center">
          <div className="text-sm font-semibold text-slate-700 mb-2 self-start">{t("ad_rc_my_qr")}</div>
          <div className="bg-white p-2 rounded-lg border"><QRCodeSVG value={myUrl} size={148} data-testid="reco-qr" /></div>
          <div className="text-[11px] text-slate-500 mt-2 break-all">{myUrl}</div>
          <Button variant="secondary" className="mt-2 w-full" onClick={copyMy} data-testid="reco-copy-link"><Copy className="h-4 w-4 mr-1" />{t("ad_rc_copy_link")}</Button>
        </Card>
      </div>
      {d.top?.length > 0 && (
        <Card className="p-4">
          <div className="text-sm font-semibold text-slate-700 mb-2">{t("ad_rc_top")}</div>
          {d.top.map((tp) => (
            <div key={tp.id || `${tp.nom}-${tp.role}`} className="flex justify-between text-sm py-1 border-b border-slate-100">
              <span>{tp.nom} <span className="text-slate-400 text-xs">· {ROLE_LABELS[tp.role] || tp.role}</span></span>
              <span className="text-slate-600">{tp.clics}{t("ad_rc_clic_s")} · {tp.filleuls}{t("ad_rc_filleul_s")}</span>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

const TYPE_ORDER = ["compte_rendu", "dossier_patient", "image_wsi"];

function Conservation() {
  const { t } = useLang();
  const { user } = useAuth();
  const canEdit = ADMIN_ROLES.includes(user?.role);
  const [policy, setPolicy] = useState(null);
  const [counts, setCounts] = useState({});
  const [items, setItems] = useState({});
  const [saving, setSaving] = useState(false);
  const [prolong, setProlong] = useState(null);
  const [pYears, setPYears] = useState(10);
  const [pJust, setPJust] = useState("");
  const [anon, setAnon] = useState([]);

  const loadDue = () => api.get("/retention/due").then((r) => { setCounts(r.data.counts); setItems(r.data.items); setPolicy(r.data.policy); }).catch(() => {});
  const loadAnon = () => api.get("/retention/anonymises").then((r) => setAnon(r.data)).catch(() => {});
  useEffect(() => { api.get("/retention/policy").then((r) => setPolicy(r.data.types)).catch(() => {}); loadDue(); loadAnon(); /* eslint-disable-next-line */ }, []);

  const setType = (ty, field, val) => setPolicy((p) => ({ ...p, [ty]: { ...p[ty], [field]: val } }));
  const save = () => {
    setSaving(true);
    api.put("/retention/policy", { types: policy })
      .then((r) => { setPolicy(r.data.types); toast.success(t("rt_saved")); loadDue(); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setSaving(false));
  };
  const anonymiser = (ty, id) => {
    if (!window.confirm(t("rt_confirm_anon"))) return;
    api.post("/retention/anonymiser", { type: ty, id })
      .then(() => { toast.success(t("rt_anon_done")); loadDue(); loadAnon(); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  const downloadRegistre = () => downloadFile("/retention/registre", "registre-conservation.pdf")
    .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  const downloadCert = (a) => downloadFile(`/retention/certificat?type=${a.type}&id=${a.item_id}`, `certificat-${a.empreinte}.pdf`)
    .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  const doProlong = () => {
    api.post("/retention/prolonger", { type: prolong.type, id: prolong.id, years: Number(pYears), justification: pJust })
      .then(() => { toast.success(t("rt_prolong_done")); setProlong(null); setPJust(""); loadDue(); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };

  if (!policy) return <div className="text-sm text-slate-400" data-testid="conservation-loading">{t("dd_loading")}</div>;
  const allItems = TYPE_ORDER.flatMap((ty) => (items[ty] || []));

  return (
    <div className="space-y-4" data-testid="conservation-panel">
      <Card className="p-3 bg-amber-50 border-amber-200 text-[12px] text-amber-900" data-testid="conservation-note">{t("rt_note")}</Card>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-slate-700">{t("rt_policy_title")}</div>
          <Button variant="secondary" className="h-9" onClick={downloadRegistre} data-testid="registre-btn"><Download className="h-4 w-4 mr-1" />{t("rt_registre_btn")}</Button>
        </div>
        <div className="space-y-2">
          {TYPE_ORDER.map((ty) => policy[ty] && (
            <div key={ty} className="flex flex-wrap items-center gap-3 border-b border-slate-100 pb-2" data-testid={`policy-row-${ty}`}>
              <div className="w-44 text-sm font-medium text-slate-700">{t(`rt_t_${ty}`)}</div>
              <div className="flex items-center gap-1.5">
                <Label className="text-xs text-slate-500">{t("rt_years")}</Label>
                <Input type="number" min={1} max={100} value={policy[ty].years} disabled={!canEdit}
                       onChange={(e) => setType(ty, "years", parseInt(e.target.value || "1", 10))}
                       className="w-20 h-9" data-testid={`policy-years-${ty}`} />
              </div>
              <div className="flex items-center gap-1.5">
                <Label className="text-xs text-slate-500">{t("rt_action")}</Label>
                <Select value={policy[ty].action} disabled={!canEdit} onValueChange={(v) => setType(ty, "action", v)}>
                  <SelectTrigger className="w-48 h-9" data-testid={`policy-action-${ty}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual_review">{t("rt_act_manual")}</SelectItem>
                    <SelectItem value="auto_anonymize">{t("rt_act_anon")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200">
                {counts[ty]?.expires ?? 0} {t("rt_expired")}
              </Badge>
            </div>
          ))}
        </div>
        {canEdit && <Button onClick={save} disabled={saving} className="mt-3 h-10" data-testid="policy-save-btn"><Save className="h-4 w-4 mr-1" />{t("rt_save")}</Button>}
      </Card>

      <Card className="p-4">
        <div className="text-sm font-semibold text-slate-700 mb-2">{t("rt_due_title")}</div>
        <Table className="clin-table" data-testid="conservation-table">
          <TableHeader><TableRow>
            <TableHead>{t("rt_th_doc")}</TableHead><TableHead>{t("rt_th_echeance")}</TableHead>
            <TableHead>{t("rt_th_remaining")}</TableHead><TableHead>{t("rt_th_status")}</TableHead>
            <TableHead>{t("rt_th_actions")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {allItems.map((it) => (
              <TableRow key={`${it.type}-${it.id}`} className={`h-9 ${it.expired ? "bg-rose-50/60" : ""}`} data-testid="conservation-row">
                <TableCell className="text-xs">{it.label}<div className="text-[10px] text-slate-400">{t(`rt_t_${it.type}`)}</div></TableCell>
                <TableCell className="font-mono text-[11px] whitespace-nowrap">{it.echeance?.slice(0, 10)}</TableCell>
                <TableCell className="text-xs">{it.jours_restants}</TableCell>
                <TableCell>
                  {it.expired
                    ? <Badge className="bg-rose-600 text-[10px]">{t("rt_expired")}</Badge>
                    : <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">{t("rt_soon")}</Badge>}
                </TableCell>
                <TableCell>
                  {canEdit && <div className="flex gap-1.5">
                    <Button size="sm" variant="secondary" className="h-7 text-[11px]" onClick={() => { setProlong({ type: it.type, id: it.id, label: it.label }); setPYears(10); setPJust(""); }} data-testid="row-prolonger-btn">{t("rt_prolonger")}</Button>
                    <Button size="sm" className="h-7 text-[11px] bg-rose-600 hover:bg-rose-700" onClick={() => anonymiser(it.type, it.id)} data-testid="row-anonymiser-btn">{t("rt_anonymiser")}</Button>
                  </div>}
                </TableCell>
              </TableRow>
            ))}
            {allItems.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">{t("rt_empty")}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      {anon.length > 0 && (
        <Card className="p-4" data-testid="anonymises-card">
          <div className="text-sm font-semibold text-slate-700 mb-2">{t("rt_anon_title")}</div>
          <Table className="clin-table" data-testid="anonymises-table">
            <TableHeader><TableRow>
              <TableHead>{t("rt_th_date")}</TableHead><TableHead>{t("rt_th_doc")}</TableHead>
              <TableHead>{t("rt_th_by")}</TableHead><TableHead>Empreinte</TableHead><TableHead>{t("rt_th_actions")}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {anon.map((a) => (
                <TableRow key={a.id} className="h-9" data-testid="anonymise-row">
                  <TableCell className="font-mono text-[11px] whitespace-nowrap">{a.date?.slice(0, 10)}</TableCell>
                  <TableCell className="text-xs">{a.label}<div className="text-[10px] text-slate-400">{t(`rt_t_${a.type}`)}</div></TableCell>
                  <TableCell className="text-xs">{a.acteur_nom || a.acteur}</TableCell>
                  <TableCell className="font-mono text-[10px] text-slate-500">{a.empreinte}</TableCell>
                  <TableCell><Button size="sm" variant="secondary" className="h-7 text-[11px]" onClick={() => downloadCert(a)} data-testid="cert-btn"><Download className="h-3.5 w-3.5 mr-1" />{t("rt_certificat")}</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={!!prolong} onOpenChange={(o) => !o && setProlong(null)}>
        <DialogContent data-testid="prolong-dialog">
          <DialogHeader><DialogTitle>{t("rt_prolong_title")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-500">{prolong?.label}</div>
            <div>
              <Label className="text-xs text-slate-500">{t("rt_prolong_years")}</Label>
              <Input type="number" min={1} max={50} value={pYears} onChange={(e) => setPYears(e.target.value)} className="h-10" data-testid="prolong-years" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">{t("rt_prolong_just")}</Label>
              <Input value={pJust} onChange={(e) => setPJust(e.target.value)} className="h-10" data-testid="prolong-just" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={doProlong} disabled={!pJust.trim()} data-testid="prolong-confirm-btn">{t("rt_prolong_btn")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Admin() {
  const { t } = useLang();
  const { user } = useAuth();
  const canUsers = ADMIN_ROLES.includes(user?.role);
  const canCosts = ["admin_technique", "chef_service", "directeur_medical", "responsable_qualite"].includes(user?.role);
  return (
    <div data-testid="admin-page">
      <PageHeader title={t("nav_admin")} subtitle={t("ad_subtitle")} />
      <Tabs defaultValue={canUsers ? "users" : "audit"}>
        <TabsList>
          {canUsers && <TabsTrigger value="users" data-testid="tab-users"><UserCog className="h-4 w-4 mr-1" />{t("ad_tab_users")}</TabsTrigger>}
          <TabsTrigger value="audit" data-testid="tab-audit">{t("ad_tab_audit")}</TabsTrigger>
          {canUsers && <TabsTrigger value="objectifs" data-testid="tab-objectifs"><Timer className="h-4 w-4 mr-1" />{t("ad_tab_objectifs")}</TabsTrigger>}
          {canUsers && <TabsTrigger value="rapport" data-testid="tab-rapport"><FileText className="h-4 w-4 mr-1" />{t("ad_tab_rapport")}</TabsTrigger>}
          {canCosts && <TabsTrigger value="couts" data-testid="tab-couts"><Coins className="h-4 w-4 mr-1" />{t("ad_tab_couts")}</TabsTrigger>}
          {canCosts && <TabsTrigger value="connexions" data-testid="tab-connexions"><Globe className="h-4 w-4 mr-1" />{t("ad_tab_connexions")}</TabsTrigger>}
          {canCosts && <TabsTrigger value="visites-fondation" data-testid="tab-visites-fondation"><Heart className="h-4 w-4 mr-1" />{t("vf_tab")}</TabsTrigger>}
          {canCosts && <TabsTrigger value="video-vues" data-testid="tab-video-vues"><PlayCircle className="h-4 w-4 mr-1" />{t("vv_tab")}</TabsTrigger>}
          {canCosts && <TabsTrigger value="conservation" data-testid="tab-conservation"><Archive className="h-4 w-4 mr-1" />{t("rt_tab")}</TabsTrigger>}
          {canCosts && <TabsTrigger value="recommandations" data-testid="tab-recommandations"><Network className="h-4 w-4 mr-1" />{t("ad_tab_reco")}</TabsTrigger>}
        </TabsList>
        {canUsers && <TabsContent value="users" className="mt-4"><Users /></TabsContent>}
        <TabsContent value="audit" className="mt-4"><Audit /></TabsContent>
        {canUsers && <TabsContent value="objectifs" className="mt-4"><Objectifs /></TabsContent>}
        {canUsers && <TabsContent value="rapport" className="mt-4"><Rapport /></TabsContent>}
        {canCosts && <TabsContent value="couts" className="mt-4"><Couts /></TabsContent>}
        {canCosts && <TabsContent value="connexions" className="mt-4"><Connexions /></TabsContent>}
        {canCosts && <TabsContent value="visites-fondation" className="mt-4"><VisitesFondation /></TabsContent>}
        {canCosts && <TabsContent value="video-vues" className="mt-4"><VideoVues /></TabsContent>}
        {canCosts && <TabsContent value="conservation" className="mt-4"><Conservation /></TabsContent>}
        {canCosts && <TabsContent value="recommandations" className="mt-4"><Recommandations /></TabsContent>}
      </Tabs>
    </div>
  );
}
